<template>
  <ContentWrap>
    <div class="mb-16px">
      <el-button
        v-hasPermi="['agentmall:level:create']"
        type="primary"
        @click="openForm()"
      >
        <Icon icon="ep:plus" class="mr-5px" />新增等级
      </el-button>
    </div>
    <el-table v-loading="loading" :data="levels" stripe>
      <el-table-column prop="rankNo" label="等级顺序" width="90" />
      <el-table-column prop="name" label="等级名称" min-width="130" />
      <el-table-column prop="code" label="等级编码" min-width="120" />
      <el-table-column label="资格费" min-width="120">
        <template #default="{ row }">{{ money(row.qualificationFee) }}</template>
      </el-table-column>
      <el-table-column label="首购折扣" width="100">
        <template #default="{ row }">{{ discount(row.firstDiscount) }}</template>
      </el-table-column>
      <el-table-column label="续费折扣" width="100">
        <template #default="{ row }">{{ discount(row.renewDiscount) }}</template>
      </el-table-column>
      <el-table-column label="权益" min-width="220">
        <template #default="{ row }">
          <el-tag v-if="row.oemEnabled" class="mr-6px">OEM</el-tag>
          <el-tag v-if="row.customDomainEnabled" class="mr-6px" type="success">自有域名</el-tag>
          <el-tag v-if="row.dividendEnabled" type="warning">分红</el-tag>
          <span v-if="!row.oemEnabled && !row.customDomainEnabled && !row.dividendEnabled"
            >基础权益</span
          >
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="{ row }"
          ><el-tag :type="row.status === 1 ? 'success' : 'info'">{{
            row.status === 1 ? '启用' : '停用'
          }}</el-tag></template
        >
      </el-table-column>
      <el-table-column label="操作" width="90" fixed="right">
        <template #default="{ row }"
          ><el-button
            v-hasPermi="['agentmall:level:update']"
            link
            type="primary"
            @click="openForm(row)"
            >编辑</el-button
          ></template
        >
      </el-table-column>
    </el-table>

    <Dialog v-model="dialogVisible" :title="form.id ? '编辑代理等级' : '新增代理等级'" width="560px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="等级编码" prop="code"><el-input v-model="form.code" :disabled="!!form.id" maxlength="32" placeholder="例如 CITY_PARTNER" /></el-form-item>
        <el-form-item label="等级名称" prop="name"
          ><el-input v-model="form.name" maxlength="32"
        /></el-form-item>
        <el-form-item label="等级顺序" prop="rankNo"
          ><el-input-number v-model="form.rankNo" :min="1" :max="99"
        /></el-form-item>
        <el-form-item label="资格费（元）" prop="qualificationFeeYuan"
          ><el-input-number v-model="form.qualificationFeeYuan" :min="0" :precision="2"
        /></el-form-item>
        <el-form-item label="首购折扣" prop="firstDiscount"
          ><el-input-number v-model="form.firstDiscount" :min="0" :max="10000" :step="100" /><span
            class="ml-8px"
            >万分比，8000 = 8 折</span
          ></el-form-item
        >
        <el-form-item label="续费折扣" prop="renewDiscount"
          ><el-input-number v-model="form.renewDiscount" :min="0" :max="10000" :step="100" /><span
            class="ml-8px"
            >0 = 全免</span
          ></el-form-item
        >
        <el-form-item label="等级权益">
          <el-checkbox v-model="form.oemEnabled">OEM 品牌</el-checkbox>
          <el-checkbox v-model="form.customDomainEnabled">自有域名</el-checkbox>
          <el-checkbox v-model="form.dividendEnabled">参与分红</el-checkbox>
        </el-form-item>
        <el-form-item label="状态"
          ><el-switch v-model="enabled" active-text="启用" inactive-text="停用"
        /></el-form-item>
      </el-form>
      <template #footer
        ><el-button @click="dialogVisible = false">取消</el-button
        ><el-button :loading="saving" type="primary" @click="submit">保存</el-button></template
      >
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import * as AgentApi from '@/api/system/agent'

defineOptions({ name: 'SystemAgentLevel' })
const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const dialogVisible = ref(false)
const formRef = ref()
const levels = ref<AgentApi.AgentLevel[]>([])
const enabled = ref(true)
const form = ref<any>({})
const rules = {
  code: [
    { required: true, message: '等级编码不能为空', trigger: 'blur' },
    { pattern: /^[A-Z][A-Z0-9_]{1,31}$/, message: '使用大写字母、数字和下划线，且以字母开头', trigger: 'blur' }
  ],
  name: [{ required: true, message: '等级名称不能为空', trigger: 'blur' }]
}
const money = (value: number) => `¥${(value / 100).toFixed(2)}`
const discount = (value: number) => (value === 0 ? '全免' : `${value / 1000} 折`)
const load = async () => {
  loading.value = true
  try {
    levels.value = await AgentApi.getLevels()
  } finally {
    loading.value = false
  }
}
const openForm = (row?: AgentApi.AgentLevel) => {
  form.value = row
    ? { ...row, qualificationFeeYuan: row.qualificationFee / 100 }
    : { code: '', name: '', rankNo: (levels.value.at(-1)?.rankNo || 0) + 1, qualificationFeeYuan: 0,
        firstDiscount: 10000, renewDiscount: 10000, oemEnabled: false,
        customDomainEnabled: false, dividendEnabled: false, status: 1 }
  enabled.value = row ? row.status === 1 : true
  dialogVisible.value = true
  nextTick(() => formRef.value?.clearValidate())
}
const submit = async () => {
  await formRef.value.validate()
  saving.value = true
  try {
    const data = {
      ...form.value,
      code: form.value.code.trim().toUpperCase(),
      qualificationFee: Math.round(form.value.qualificationFeeYuan * 100),
      status: enabled.value ? 1 : 0
    }
    if (form.value.id) await AgentApi.updateLevel(data)
    else await AgentApi.createLevel(data)
    message.success(form.value.id ? '等级配置已保存，新配置仅影响后续订单' : '代理等级已新增')
    dialogVisible.value = false
    await load()
  } finally {
    saving.value = false
  }
}
onMounted(load)
</script>
